import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Wallet,
  Landmark,
  TrendingUp,
  CheckCircle2,
  Users,
  LockKeyhole,
  BarChart3,
  Headphones,
  BadgeCheck,
  Clock3,
  FileCheck2,
  Banknote,
  Building2,
} from "lucide-react";

import serviceLoanImg from "../assets/img/growth-success3.png";
import serviceInvestImg from "../assets/img/investment-card_1.png";
import servicePlanningImg from "../assets/img/financial-planning1.png";

import flyer1 from "../assets/img/car-for-cash.png";
import flyer2 from "../assets/img/property-loan.png";
import flyer3 from "../assets/img/civil-defence-loan.png";
import flyer4 from "../assets/img/partner-with-us.png";

import "../assets/css/services.css";

const services = [
  {
    icon: Wallet,
    title: "Salary Advance Loans",
    label: "Fast Lending",
    image: serviceLoanImg,
    text: "Quick salary-backed lending for government workers with predictable income and structured repayment.",
    points: ["Fast disbursement", "Salary-linked repayment", "Simple verification"],
  },
  {
    icon: Landmark,
    title: "Collateral-Backed Lending",
    label: "Secured Finance",
    image: serviceInvestImg,
    text: "Vehicle and property-backed lending designed to reduce exposure and protect capital.",
    points: ["Vehicle-backed loans", "Property-backed loans", "Lower risk exposure"],
  },
  {
    icon: TrendingUp,
    title: "P2P Investment Structuring",
    label: "Investor Returns",
    image: servicePlanningImg,
    text: "A structured model connecting investors with vetted borrowers and predictable monthly returns.",
    points: ["Capital deployment", "Monthly returns", "Transparent monitoring"],
  },
];

const processSteps = [
  {
    icon: Users,
    title: "Make Enquiry",
    text: "Borrowers or investors contact GMT to begin the process.",
  },
  {
    icon: FileCheck2,
    title: "Review & Verify",
    text: "GMT reviews documents, borrower profile, and repayment structure.",
  },
  {
    icon: Banknote,
    title: "Deploy Funds",
    text: "Funds are deployed to carefully vetted borrowers.",
  },
  {
    icon: BarChart3,
    title: "Monitor Returns",
    text: "Repayment and investor returns are tracked continuously.",
  },
];

const supportCards = [
  {
    icon: LockKeyhole,
    title: "Risk Management",
    text: "Strict borrower screening, salary-linked repayment, collateral support, and monitoring.",
  },
  {
    icon: BarChart3,
    title: "Loan Monitoring",
    text: "Technology-driven tracking improves repayment discipline and reduces delays.",
  },
  {
    icon: Headphones,
    title: "Human Support",
    text: "Our team guides borrowers and investors from enquiry to completion.",
  },
];

export default function Services() {
  return (
    <main className="services-page">
      <section className="services-hero">
        <div className="services-hero-copy">
          <span>GMT Lending Services</span>
          <h1>Fintech lending solutions built for speed, security and growth</h1>
          <p>
            Access salary advance loans, secured lending, peer-to-peer
            investment structuring, and advisory through a transparent,
            technology-driven finance model.
          </p>

          <div className="services-hero-actions">
            <Link to="/applyloan" className="services-primary-btn">
              Apply for Loan <ArrowRight size={16} />
            </Link>

            <Link to="/investment" className="services-secondary-btn">
              View Investment Plans <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="services-dashboard-card">
          <div className="dashboard-top">
            <div>
              <span>Service Overview</span>
              <h3>Structured Lending</h3>
            </div>
            <ShieldCheck size={28} />
          </div>

          <div className="dashboard-metric">
            <span>Investor ROI</span>
            <strong>3% Monthly</strong>
          </div>

          <div className="dashboard-progress">
            <span />
          </div>

          <div className="dashboard-grid">
            <div>
              <Wallet />
              <strong>Salary Loans</strong>
              <span>Fast access</span>
            </div>

            <div>
              <Landmark />
              <strong>Secured Loans</strong>
              <span>Asset-backed</span>
            </div>
          </div>

          <div className="dashboard-note">
            <BadgeCheck size={18} />
            Built on technology, transparency and disciplined risk management.
          </div>
        </div>
      </section>

      <section className="services-trust-strip">
        <div>
          <Clock3 />
          <strong>Fast Review</strong>
          <span>Simple finance process</span>
        </div>

        <div>
          <ShieldCheck />
          <strong>Secured Structure</strong>
          <span>Risk-managed lending</span>
        </div>

        <div>
          <Building2 />
          <strong>Business Support</strong>
          <span>For salary earners and SMEs</span>
        </div>
      </section>

      <section className="services-grid-section">
        <div className="services-section-heading">
          <span>What We Offer</span>
          <h2>Financial services designed for real customer needs</h2>
        </div>

        <div className="services-grid">
          {services.map((service) => {
            const Icon = service.icon;

            return (
              <article className="service-modern-card" key={service.title}>
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>

                <div className="service-content">
                  <div className="service-label">
                    <Icon size={18} />
                    {service.label}
                  </div>

                  <h3>{service.title}</h3>
                  <p>{service.text}</p>

                  <ul>
                    {service.points.map((point) => (
                      <li key={point}>
                        <CheckCircle2 size={17} />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="service-process-section">
        <div className="process-copy">
          <span>How It Works</span>
          <h2>A simple process built around discipline and transparency</h2>
          <p>
            GMT reduces delays, improves loan monitoring, and ensures timely
            repayment through structured systems.
          </p>
        </div>

        <div className="process-steps">
          {processSteps.map((step, index) => {
            const Icon = step.icon;

            return (
              <div className="process-card" key={step.title}>
                <strong>0{index + 1}</strong>
                <div className="process-icon">
                  <Icon size={22} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="support-section">
        <div className="services-section-heading">
          <span>Built for Confidence</span>
          <h2>Structured to protect trust, repayment and performance</h2>
        </div>

        <div className="support-grid">
          {supportCards.map((item) => {
            const Icon = item.icon;

            return (
              <div className="support-card" key={item.title}>
                <Icon />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>
      

      <section className="services-cta">
        <div>
          <span>Ready to Get Started?</span>
          <h2>Access lending support or grow your investment with GMT</h2>
          <p>
            Speak with our team today and take the next step with a reliable
            financial technology partner.
          </p>
        </div>

        <Link to="/contact" className="services-white-btn">
          Contact Us <ArrowRight size={16} />
        </Link>
      </section>
    </main>
  );
}